<template>
  <section class="my-tariff mt-5">
    <h1 class="my-tariff__title">
      Мой тариф
    </h1>

    <navigation-tabs class="mb-4">
      <template #middle>
        <span class="my-tariff__hint">
          Ваш тарифный план, баланс и подключённые модули
        </span>
      </template>
    </navigation-tabs>

    <loader v-if="loading" />

    <template v-else>
      <promo-banner class="my-tariff__banner" />

      <div class="my-tariff__row">
        <tariff-plan-card
          class="my-tariff__plan"
          :tariff="tariff"
          :daily-rate="paymentAccount.dailyRate || 0"
          :monthly-rate="paymentAccount.monthlyRate || 0"
          :filials-count="paymentAccount.filialsCount || 0"
          :connected-at="paymentAccount.connectedAt"
          :blocked="paymentAccount.blocked"
        />

        <payment-method-summary
          v-if="canManage"
          class="my-tariff__method"
          :method="defaultMethod"
          :can-manage="canManage"
          :updating="autopayUpdating"
          @toggle-autopay="toggleAutopay"
        />
      </div>

      <balance-forecast-card
        class="my-tariff__forecast"
        :days-left="paymentAccount.daysLeft"
        :valid-until="paymentAccount.validUntil"
        :expiring-soon="paymentAccount.expiringSoon"
        :warning-days="paymentAccount.expirationWarningDays"
        :attention-days="paymentAccount.expirationAttentionDays"
        :promised-payment="paymentAccount.promisedPayment"
        :can-manage="canManage"
        :activating="activating"
        @activate-promised="activatePromised"
      />

      <tariff-features-card
        v-if="features.length"
        class="my-tariff__features"
        :tariff-name="tariffName"
        :features="features"
      />
    </template>

    <div class="my-tariff__support">
      Вопрос по тарифу или списаниям — напишите нам в чат поддержки
    </div>
  </section>
</template>

<script>
  import { mapState } from 'vuex'
  import Loader from '@/components/loader/Loader'
  import SnackbarMessage from '@/mixins/snackbar-message'
  import NavigationTabs from '@/components/billing/NavigationTabs.vue'
  import PromoBanner from '@/components/billing/my-tariff/PromoBanner.vue'
  import TariffPlanCard from '@/components/billing/my-tariff/TariffPlanCard.vue'
  import BalanceForecastCard from '@/components/billing/my-tariff/BalanceForecastCard.vue'
  import PaymentMethodSummary from '@/components/billing/my-tariff/PaymentMethodSummary.vue'
  import TariffFeaturesCard from '@/components/billing/my-tariff/TariffFeaturesCard.vue'
  import CRMPermission from '@/models/enums/permissions/CRMPermission'
  import RouteName from '@/models/enums/RouteName'
  import billingApi from '@/api/billing'

  export default {
    name: 'MyTariff',
    components: {
      NavigationTabs,
      Loader,
      PromoBanner,
      TariffPlanCard,
      BalanceForecastCard,
      PaymentMethodSummary,
      TariffFeaturesCard,
    },
    mixins: [SnackbarMessage],
    data () {
      return {
        loading: false,
        autopayUpdating: false,
        activating: false,
        defaultMethod: null,
        tariff: null,
      }
    },
    computed: {
      ...mapState('billing', ['paymentAccount']),
      canManage () {
        return this.userCan(CRMPermission.ManagePaymentAccount)
      },
      tariffName () {
        return this.tariff ? this.tariff.name : null
      },
      features () {
        return (this.tariff && this.tariff.features) || []
      },
    },
    created () {
      this.load()
    },
    methods: {
      async load () {
        this.loading = true
        try {
          const [, tariff] = await Promise.all([
            this.$store.dispatch('billing/fetchPaymentAccount', { force: true }),
            billingApi.fetchTariff(),
            // Карты лежат за тем же правом, что и управление счётом: без него запрос отдаст 403
            this.canManage ? this.loadDefaultMethod() : null,
          ])
          this.tariff = tariff
        } catch (err) {
          this.showErrorFromResponse(err)
        } finally {
          this.loading = false
        }
      },
      async loadDefaultMethod () {
        const methods = await billingApi.fetchPaymentMethods()
        this.defaultMethod = methods.find((method) => method.isDefault) || null
      },
      async activatePromised () {
        if (this.activating) return

        this.activating = true
        try {
          await billingApi.activatePromisedPayment()
          this.$router.push({ name: RouteName.BillingTopupResult, query: { promised: '1' } })
        } catch (err) {
          this.showErrorFromResponse(err)
        } finally {
          this.activating = false
        }
      },
      async toggleAutopay (enabled) {
        if (!this.defaultMethod || this.autopayUpdating) return

        this.autopayUpdating = true
        try {
          const id = this.defaultMethod.id
          this.defaultMethod = enabled
            ? await billingApi.enableAutopay(id)
            : await billingApi.disableAutopay(id)
        } catch (err) {
          this.showErrorFromResponse(err)
          // Тумблер уже перерисовался под клик — возвращаем его к тому, что на бэке
          await this.loadDefaultMethod()
        } finally {
          this.autopayUpdating = false
        }
      },
    },
  }
</script>

<style scoped lang="less">
.my-tariff {
  padding-left: 4px;
  padding-right: 4px;

  &__title {
    font-size: 24px;
    font-weight: 700;
    color: #323030;
    margin-bottom: 20px;
  }

  &__hint {
    font-size: 13px;
    color: #323030;
  }

  &__banner {
    margin-bottom: 20px;
  }

  &__row {
    display: flex;
    align-items: stretch;
    gap: 16px;
    margin-bottom: 20px;

    .media-md({
      flex-direction: column;
    });
  }

  &__plan {
    flex: 1 1 66%;
    min-width: 0;
  }

  &__method {
    flex: 1 1 33%;
    min-width: 320px;

    .media-md({
      min-width: 0;
    });
  }

  &__forecast {
    margin-bottom: 20px;
  }

  &__support {
    margin-top: 20px;
    font-size: 13px;
    color: #949191;
  }
}
</style>
